import { TableColumn, Link } from '@backstage/core-components';
import { Chip } from '@material-ui/core';
import type { SecurityIssue } from '../api/types';
import { formatToolName } from '../utils/textFormatting';
import { toSafeHttpHref } from '../utils/urlSafety';
import { AssetCell, CveCell, TaxonomyCell, TeamCell } from './EntityCells';

export const centered = { headerStyle: { textAlign: 'center' as const } };

/**
 * The canonical column set shared by `GroupBySummaryPanel`'s
 * team/pillarCategory/issueType/cve modes and its asset-drilldown table,
 * plus `EntityGroupSecurityTab`'s own team-mode table — so those tables
 * show the same columns in the same order instead of drifting apart.
 * `dupColumn` and the actions column are appended by each caller, since
 * whether they apply depends on caller-specific state
 * (`filters.deduplicate`, quiesce permissions).
 *
 * Not yet migrated onto this: `LicenseCompliancePage.tsx`,
 * `SecurityPostureReportPage.tsx`, and
 * `securityControls/TaxonomyFindingsTable.tsx` still hand-roll their own
 * near-identical column arrays (tracked as follow-up work, not part of
 * this PR) — a new Tool column change here won't reach those tables.
 */
export const buildFindingColumns = (
  existingRefs: ReadonlySet<string>,
  pillarLabels: ReadonlyMap<string, string>,
  categoryLabels: ReadonlyMap<string, string>,
): TableColumn<SecurityIssue>[] => [
  {
    title: 'ID',
    field: 'src_id',
    ...centered,
    render: (row: SecurityIssue) => {
      const href = toSafeHttpHref(row.src_issue_url);
      return href ? <Link to={href}>{row.src_id}</Link> : row.src_id;
    },
  },
  { title: 'Severity', field: 'severity', width: '80px', ...centered },
  {
    title: 'Team',
    field: 'team',
    ...centered,
    render: (row: SecurityIssue) => (
      <TeamCell team={row.team} existingRefs={existingRefs} />
    ),
  },
  { title: 'Title', field: 'issue_title', ...centered },
  {
    title: 'Asset',
    field: 'asset',
    ...centered,
    render: (row: SecurityIssue) => (
      <AssetCell
        asset={row.asset}
        issueType={row.issue_type}
        existingRefs={existingRefs}
      />
    ),
  },
  { title: 'Created At', field: 'created_at', type: 'date', ...centered },
  {
    title: 'CVE/Rule',
    field: 'cve_or_rule',
    ...centered,
    render: (row: SecurityIssue) => <CveCell value={row.cve_or_rule} />,
  },
  {
    title: 'Tool',
    field: 'issue_type',
    ...centered,
    render: (row: SecurityIssue) => formatToolName(row.issue_type),
  },
  {
    title: 'Pillar / Category',
    field: 'security_issue_pillar',
    ...centered,
    render: (row: SecurityIssue) => (
      <TaxonomyCell
        pillar={row.security_issue_pillar}
        category={row.security_issue_category}
        pillarLabels={pillarLabels}
        categoryLabels={categoryLabels}
      />
    ),
  },
];

export const dupColumn: TableColumn<SecurityIssue> = {
  title: 'Dups',
  field: 'dup_count',
  width: '70px',
  sorting: false,
  ...centered,
  render: (row: SecurityIssue) =>
    row.dup_count && row.dup_count > 1 ? (
      <Chip
        size="small"
        label={`${row.dup_index} of ${row.dup_count}`}
        variant="outlined"
      />
    ) : null,
};
